import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import AdminSidebar from '@/components/AdminSidebar';
import AdminSessionMonitor from '@/components/AdminSessionMonitor';
import StatsOverview from '@/components/admin/StatsOverview';
import { CommandPalette } from '@/components/admin/CommandPalette';
import { DraggableDashboard } from '@/components/admin/DraggableDashboard';
import { InteractiveChart } from '@/components/admin/InteractiveChart';
import { DashboardSettings } from '@/components/admin/DashboardSettings';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useAdminPreferences } from '@/hooks/useAdminPreferences';
import { useAdminRealtimeUpdates } from '@/hooks/useAdminRealtimeUpdates';
import { Layout } from 'react-grid-layout';
import UserManagement from '@/components/admin/UserManagement';
import BorderReports from '@/components/admin/BorderReports';
import RewardPoints from '@/components/admin/RewardPoints';
import AdvertisersManagement from '@/components/admin/AdvertisersManagement';
import AdminSettings from '@/components/admin/AdminSettings';
import SecurityReports from '@/components/admin/SecurityReports';
import { SecurityDashboard } from '@/components/admin/SecurityDashboard';
import { MadisonKnowledgeBase } from '@/components/admin/MadisonKnowledgeBase';
import AISettings from '@/components/admin/AISettings';
import UsageAnalytics from '@/components/admin/UsageAnalytics';
import MessagesManagement from '@/components/admin/MessagesManagement';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminTrackingSummary from '@/pages/AdminTrackingSummary';
import AdminPromosManagement from '@/pages/AdminPromosManagement';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface UserData {
  id: string;
  full_name: string;
  email: string;
  phone_number?: string;
  created_at: string;
  last_login?: string;
  status: string;
  total_points?: number;
}

interface SponsorData {
  id: string;
  business_name: string;
  category: string;
  location: string;
  is_active: boolean;
  offer_text: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
}

const AdminDashboard = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('summary');
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { preferences, updateDashboardLayout } = useAdminPreferences();
  
  // Real-time updates hook
  const {
    stats,
    recentEvents,
    isConnected,
    loading: realtimeLoading,
    userChartData,
    checkinChartData,
    refreshData
  } = useAdminRealtimeUpdates(isSpanish);

  // User management state
  const [users, setUsers] = useState<UserData[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pointsAdjustment, setPointsAdjustment] = useState('');
  const [pointsReason, setPointsReason] = useState('');

  // Border reports state
  const [borderReports, setBorderReports] = useState<any[]>([]);
  const [reportSearchTerm, setReportSearchTerm] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [reportStatusFilter, setReportStatusFilter] = useState('all');

  // Reward points state
  const [userPoints, setUserPoints] = useState<any[]>([]);
  const [pointsSearchTerm, setPointsSearchTerm] = useState('');

  // Advertisers state
  const [advertisers, setAdvertisers] = useState<any[]>([]);
  const [advertiserSearchTerm, setAdvertiserSearchTerm] = useState('');
  const [advertiserStatusFilter, setAdvertiserStatusFilter] = useState('all');
  const [advertiserPlacementFilter, setAdvertiserPlacementFilter] = useState('all');
  const [editingAdvertiser, setEditingAdvertiser] = useState<any>(null);
  const [newAdvertiserData, setNewAdvertiserData] = useState<any>({});

  // Settings state
  const [settings, setSettings] = useState({
    defaultLanguage: 'en',
    geofenceRadius: 500,
    pointsPerCheckin: 5,
    pointsPerPhoto: 10,
    pointsPerCheckout: 5,
    enableNotifications: true,
    requirePhotoApproval: false,
    maxDailyPoints: 100,
    sessionTimeout: 30
  });

  // Remove fetchDashboardData - now handled by useAdminRealtimeUpdates hook

  // Keyboard shortcuts for quick navigation
  useKeyboardShortcuts([
    { key: '1', callback: () => setActiveSection('summary'), description: 'Summary' },
    { key: '2', callback: () => setActiveSection('analytics'), description: 'Analytics' },
    { key: '3', callback: () => setActiveSection('usage'), description: 'Usage Analytics' },
    { key: '4', callback: () => setActiveSection('users'), description: 'Users' },
    { key: '5', callback: () => setActiveSection('reports'), description: 'Border Reports' },
    { key: '6', callback: () => setActiveSection('points'), description: 'Reward Points' },
    { key: '7', callback: () => setActiveSection('advertisers'), description: 'Advertisers' },
    { key: '8', callback: () => setActiveSection('promos-management'), description: 'Promos Management' },
    { key: '9', callback: () => setActiveSection('messages'), description: 'Daily Messages' },
    { key: 'k', ctrlKey: true, callback: () => setCommandPaletteOpen(true), description: 'Open Command Palette' },
    { key: 'b', ctrlKey: true, callback: () => {}, description: 'Toggle Sidebar' }, // Handled by SidebarTrigger
  ]);


  // Handler functions
  const handleAdjustUserPoints = async () => {
    if (!selectedUserId || !pointsAdjustment || !pointsReason) return;
    
    try {
      const adjustment = parseInt(pointsAdjustment);
      // Here you would implement the actual points adjustment logic
      toast.success(isSpanish ? 'Puntos ajustados exitosamente' : 'Points adjusted successfully');
      setPointsAdjustment('');
      setPointsReason('');
      setSelectedUserId(null);
    } catch (error) {
      toast.error(isSpanish ? 'Error al ajustar puntos' : 'Error adjusting points');
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      // Here you would save settings to the database
      toast.success(isSpanish ? 'Configuración guardada' : 'Settings saved');
    } catch (error) {
      toast.error(isSpanish ? 'Error al guardar configuración' : 'Error saving settings');
    }
  };

  const renderActiveSection = () => {

    switch (activeSection) {
      case 'summary':
        const dashboardWidgets = [
          {
            id: 'stats',
            title: isSpanish ? 'Estadísticas Generales' : 'General Statistics',
            component: <StatsOverview stats={stats} isSpanish={isSpanish} />,
            defaultLayout: { i: 'stats', x: 0, y: 0, w: 12, h: 3 },
          },
          {
            id: 'users-chart',
            title: isSpanish ? 'Nuevos Usuarios' : 'New Users',
            component: (
              <InteractiveChart
                data={userChartData}
                title={isSpanish ? 'Registros por Día' : 'Registrations per Day'}
                type="line"
                drillDownEnabled
                onDrillDown={(point) => console.log('Drill down:', point)}
                isSpanish={isSpanish}
              />
            ),
            defaultLayout: { i: 'users-chart', x: 0, y: 3, w: 6, h: 4 },
          },
          {
            id: 'checkins-chart',
            title: isSpanish ? 'Check-ins Diarios' : 'Daily Check-ins',
            component: (
              <InteractiveChart
                data={checkinChartData}
                title={isSpanish ? 'Check-ins por Día' : 'Check-ins per Day'}
                type="bar"
                drillDownEnabled
                isSpanish={isSpanish}
              />
            ),
            defaultLayout: { i: 'checkins-chart', x: 6, y: 3, w: 6, h: 4 },
          },
        ];

        return (
          <DraggableDashboard
            widgets={dashboardWidgets}
            onLayoutChange={updateDashboardLayout}
            savedLayout={preferences.dashboardLayout}
            isSpanish={isSpanish}
          />
        );
      
      case 'analytics':
        return <AdminAnalytics />;
      
      case 'usage':
        return <UsageAnalytics />;
      
      case 'users':
        return (
          <UserManagement
            users={users}
            searchTerm={userSearchTerm}
            statusFilter={userStatusFilter}
            selectedUserForPoints={selectedUserId}
            pointsAdjustment={pointsAdjustment}
            pointsReason={pointsReason}
            onSearchChange={setUserSearchTerm}
            onStatusFilterChange={setUserStatusFilter}
            onViewUser={(userId) => console.log('View user:', userId)}
            onSetSelectedUserForPoints={setSelectedUserId}
            onPointsAdjustmentChange={setPointsAdjustment}
            onPointsReasonChange={setPointsReason}
            onAdjustUserPoints={handleAdjustUserPoints}
            isSpanish={isSpanish}
          />
        );
      
      case 'reports':
        return (
          <BorderReports
            reports={borderReports}
            searchTerm={reportSearchTerm}
            typeFilter={reportTypeFilter}
            statusFilter={reportStatusFilter}
            onSearchChange={setReportSearchTerm}
            onTypeFilterChange={setReportTypeFilter}
            onStatusFilterChange={setReportStatusFilter}
            onFlagReport={(id) => console.log('Flag report:', id)}
            onRemoveReport={(id) => console.log('Remove report:', id)}
            onViewReport={(id) => console.log('View report:', id)}
            isSpanish={isSpanish}
          />
        );
      
      case 'points':
        return (
          <RewardPoints
            userPoints={userPoints}
            searchTerm={pointsSearchTerm}
            selectedUserId={selectedUserId}
            pointsAdjustment={pointsAdjustment}
            pointsReason={pointsReason}
            onSearchChange={setPointsSearchTerm}
            onUserSelect={setSelectedUserId}
            onPointsAdjustmentChange={setPointsAdjustment}
            onPointsReasonChange={setPointsReason}
            onAdjustPoints={handleAdjustUserPoints}
            isSpanish={isSpanish}
          />
        );
      
      case 'advertisers':
        return (
          <AdvertisersManagement
            isSpanish={isSpanish}
          />
        );
      
      case 'promos-management':
        return <AdminPromosManagement />;
      
      case 'madison-kb':
        return <MadisonKnowledgeBase />;
      
      case 'ai-settings':
        return <AISettings />;
      
      case 'messages':
        return <MessagesManagement />;
      
      case 'settings':
        return <DashboardSettings isSpanish={isSpanish} />;
      
      case 'security':
        return <SecurityDashboard />;
      
      case 'tracking-summary':
        return <AdminTrackingSummary />;
      
      default:
        return <StatsOverview stats={stats} isSpanish={isSpanish} />;
    }
  };

  if (realtimeLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900/20 via-background to-orange-900/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {isSpanish ? 'Cargando panel de administración...' : 'Loading admin dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminSessionMonitor />
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onSectionChange={setActiveSection}
        isSpanish={isSpanish}
      />
      <SidebarProvider defaultOpen>
        <div className="flex min-h-screen w-full bg-background">
          {/* Global sidebar trigger in header */}
          <header className="fixed top-0 left-0 right-0 h-14 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50 flex items-center justify-between px-4">
            <div className="flex items-center">
              <SidebarTrigger />
              <h1 className="ml-4 text-lg font-semibold">
                {isSpanish ? 'Panel de Administración' : 'Admin Dashboard'}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors",
                isConnected ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"
              )}>
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  isConnected ? "bg-green-500 animate-pulse" : "bg-red-500"
                )} />
                <span className="font-medium">
                  {isConnected 
                    ? (isSpanish ? 'En vivo' : 'Live')
                    : (isSpanish ? 'Desconectado' : 'Disconnected')
                  }
                </span>
              </div>
              
              {recentEvents.length > 0 && (
                <div className="relative">
                  <div className="w-2 h-2 rounded-full bg-red-500 absolute -top-1 -right-1 animate-ping" />
                  <div className="w-2 h-2 rounded-full bg-red-500 absolute -top-1 -right-1" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {recentEvents.length} {isSpanish ? 'nuevos' : 'new'}
                  </span>
                </div>
              )}
            </div>
          </header>

          <AdminSidebar 
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            isSpanish={isSpanish}
          />
          
          <main className="flex-1 overflow-auto pt-14">
            <div className="container mx-auto p-8 max-w-[1800px] space-y-8">
              {renderActiveSection()}
            </div>
          </main>
        </div>
      </SidebarProvider>
    </>
  );
};

export default AdminDashboard;